import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faBars,
    faTimes,
    faUser,
    faSignOutAlt,
    faCreditCard,
    faTachometerAlt,
    faCog,
    faQuestionCircle,
    faFlask,
    faMoon,
    faSun,
} from '@fortawesome/free-solid-svg-icons';
import { useLayout } from '../../contexts/LayoutContext';
import { useDarkMode } from '../../hooks/useDarkMode';
import { useUserProfile } from '../../contexts/user/hooks';
import { NotificationBell } from '../ui/NotificationBell';

interface NavSubItem {
    to: string;
    label: string;
    icon: IconDefinition;
}

interface NavItem {
    to?: string;
    label: string;
    icon: IconDefinition;
    subItems?: NavSubItem[];
}

const navItems: NavItem[] = [
    { to: '/app', label: 'Dashboard', icon: faTachometerAlt },
    { to: '/app/backtest-studio', label: 'Backtest Studio', icon: faFlask },
];

const MobileTopBar: React.FC = () => {
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const { state, toggleMobileMenu, setMobileMenuOpen } = useLayout();
    const { dark, toggleDarkMode } = useDarkMode();
    const { profile } = useUserProfile();
    const navigate = useNavigate();
    const location = useLocation();
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMobileMenuOpen(false);
                setUserDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [setMobileMenuOpen]);

    // Close menu when route changes
    useEffect(() => {
        setMobileMenuOpen(false);
        setUserDropdownOpen(false);
    }, [location.pathname, setMobileMenuOpen]);

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        navigate('/login');
    };

    const isActiveRoute = (to: string) => {
        return location.pathname === to || location.pathname.startsWith(to + '/');
    };

    return (
        <>
            {/* Fixed Top Bar */}
            <header className="fixed top-0 left-0 right-0 h-16 bg-[#1e1e2e] border-b border-white/[0.06] z-50">
                <div className="flex items-center justify-between h-full px-4">
                    {/* Left: Menu + Logo */}
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={toggleMobileMenu}
                            className="p-2 rounded-md hover:bg-white/10 transition-colors"
                        >
                            <FontAwesomeIcon
                                icon={state.mobileMenuOpen ? faTimes : faBars}
                                className="w-5 h-5 text-gray-400"
                            />
                        </button>
                        <Link to="/dashboard" className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-sm">Q</span>
                            </div>
                            <span className="font-semibold text-white">Quanthop</span>
                        </Link>
                    </div>

                    {/* Right: Dark Mode Toggle + User Menu */}
                    <div className="flex items-center space-x-2">
                        {/* Dark Mode Toggle */}
                        <button
                            onClick={toggleDarkMode}
                            className="p-2 rounded-md hover:bg-white/10 transition-colors"
                            title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            <FontAwesomeIcon
                                icon={dark ? faSun : faMoon}
                                className="w-5 h-5 text-gray-400"
                            />
                        </button>

                        {/* Notifications */}
                        <NotificationBell />

                        {/* User Menu */}
                        <div className="relative" ref={menuRef}>
                            <button
                                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                                className="flex items-center space-x-2 p-2 rounded-md hover:bg-white/10 transition-colors"
                            >
                                <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                                    <FontAwesomeIcon icon={faUser} className="w-4 h-4 text-gray-400" />
                                </div>
                            </button>

                            {/* User Dropdown */}
                            {userDropdownOpen && (
                                <div className="absolute right-0 top-full mt-2 w-48 bg-[#1e1e2e] border border-white/[0.06] rounded-md shadow-lg">
                                    <div className="p-3 border-b border-white/[0.06]">
                                        <p className="text-sm font-medium text-white">
                                            {profile?.username || profile?.email || 'User'}
                                        </p>
                                        <p className="text-xs text-gray-400 capitalize">
                                            {profile?.tier || 'Standard'} Plan
                                        </p>
                                    </div>
                                    <Link
                                        to="/profile"
                                        className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/10"
                                    >
                                        <FontAwesomeIcon icon={faUser} className="w-4 h-4 mr-3" />
                                        Profile
                                    </Link>
                                    <Link
                                        to="/billing"
                                        className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/10"
                                    >
                                        <FontAwesomeIcon icon={faCreditCard} className="w-4 h-4 mr-3" />
                                        Billing
                                    </Link>
                                    <Link
                                        to="/help"
                                        className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/10"
                                    >
                                        <FontAwesomeIcon icon={faQuestionCircle} className="w-4 h-4 mr-3" />
                                        Help
                                    </Link>
                                    <Link
                                        to="/settings"
                                        className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/10"
                                    >
                                        <FontAwesomeIcon icon={faCog} className="w-4 h-4 mr-3" />
                                        Settings
                                    </Link>
                                    <hr className="border-white/[0.06]" />
                                    <button
                                        onClick={handleLogout}
                                        className="w-full flex items-center px-4 py-2 text-sm text-red-400 hover:bg-white/10"
                                    >
                                        <FontAwesomeIcon icon={faSignOutAlt} className="w-4 h-4 mr-3" />
                                        Sign Out
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Menu Overlay */}
            {state.mobileMenuOpen && (
                <div className="fixed inset-0 z-40 lg:hidden">
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50"
                        onClick={() => setMobileMenuOpen(false)}
                    />

                    {/* Slide-out Menu */}
                    <div className="fixed top-16 left-0 bottom-0 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out">
                        <nav className="p-4 space-y-2 overflow-y-auto h-full">
                            {navItems.map((item) => (
                                <div key={item.label}>
                                    {item.to ? (
                                        <Link
                                            to={item.to}
                                            className={`
                        flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                        ${isActiveRoute(item.to)
                                                    ? 'bg-blue-900/50 text-blue-300'
                                                    : 'text-gray-300 hover:bg-white/10'
                                                }
                      `}
                                        >
                                            <FontAwesomeIcon
                                                icon={item.icon}
                                                className={`
                          w-5 h-5 mr-3 transition-colors
                          ${isActiveRoute(item.to)
                                                        ? 'text-blue-300'
                                                        : 'text-gray-400'
                                                    }
                        `}
                                            />
                                            {item.label}
                                        </Link>
                                    ) : (
                                        <>
                                            <div className="flex items-center px-3 py-2 text-sm font-medium text-gray-300">
                                                <FontAwesomeIcon
                                                    icon={item.icon}
                                                    className="w-5 h-5 mr-3 text-gray-400"
                                                />
                                                {item.label}
                                            </div>
                                            {item.subItems && (
                                                <div className="ml-6 space-y-1">
                                                    {item.subItems.map((subItem) => (
                                                        <Link
                                                            key={subItem.to}
                                                            to={subItem.to}
                                                            className={`
                                flex items-center px-3 py-2 text-sm rounded-md transition-colors
                                ${isActiveRoute(subItem.to)
                                                                    ? 'bg-blue-900/50 text-blue-300'
                                                                    : 'text-gray-400 hover:bg-white/10'
                                                                }
                              `}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={subItem.icon}
                                                                className={`
                                  w-4 h-4 mr-3 transition-colors
                                  ${isActiveRoute(subItem.to)
                                                                        ? 'text-blue-300'
                                                                        : 'text-gray-400'
                                                                    }
                                `}
                                                            />
                                                            {subItem.label}
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            ))}
                        </nav>
                    </div>
                </div>
            )}
        </>
    );
};

export default MobileTopBar;
